// =============================================
// coaTemplateApi — typed client for /api/bookkeeping/*.
// Business-type → starting Chart of Accounts: read the merged template for a business
// type, and instantiate a (user-editable) staged list into the owner's acctg_coa.
// Mirrors payrollApi: bearer token from localStorage, JSON in/out. Source of truth is
// PostgreSQL; there is no localStorage cache for these calls.
// =============================================
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
function token(): string | null { return localStorage.getItem('akt_auth_token'); }

async function call<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const r = await fetch(`${API_BASE}/bookkeeping${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({} as any));
    const e: any = new Error(body.message || `Request failed (${r.status})`);
    e.code = r.status;
    throw e;
  }
  return r.json();
}

export interface TemplateAccount {
  template_id: number;
  template_code: string;
  scope: 'core' | 'category' | 'type';
  account_kind: 'asset' | 'liability' | 'equity' | 'income' | 'other_income' | 'cogs' | 'expense';
  suggested_number: string;
  account_name: string;
  account_description: string;
  finsta_cate_id: number;
  finsta_cate_name: string;
  normal_balance: 'Debit' | 'Credit';
  preselected: boolean;
}

export interface TemplateSection {
  key: string;
  name: string;
  normal_balance: 'Debit' | 'Credit';
  accounts: TemplateAccount[];
}

export interface BusiTypeFlags {
  has_inventory: boolean;
  has_cogs: boolean;
  handles_tips: boolean;
  has_gift_cards: boolean;
  uses_job_costing: boolean;
}

export interface CoaTemplateResponse {
  busi_type: {
    busi_type_id: number;
    type_code: string;
    type_name: string;
    naics_code: string | null;
    busi_category_id: number;
    category_code: string;
    category_name: string;
    flags: BusiTypeFlags;
  };
  sections: TemplateSection[];
}

export interface BusiCategory {
  busi_category_id: number;
  category_code: string;
  category_name: string;
  sort_order: number;
}
export interface BusiType {
  busi_type_id: number;
  busi_category_id: number;
  type_code: string;
  type_name: string;
  naics_code: string | null;
  has_inventory: number;
  has_cogs: number;
  handles_tips: number;
  has_gift_cards: number;
  uses_job_costing: number;
  sort_order: number;
}

/** Reference list for the Type of Business picker (categories + all types). */
export function getBusinessTypes(): Promise<{ categories: BusiCategory[]; types: BusiType[] }> {
  return call(`/business-types`);
}

/** Fetch the merged 3-layer starting-COA template for a business type. */
export function getCoaTemplate(busiTypeId: number): Promise<CoaTemplateResponse> {
  return call<CoaTemplateResponse>(`/coa-template?busi_type_id=${encodeURIComponent(busiTypeId)}`);
}

export interface CreateFromTemplateAccount {
  account_kind: TemplateAccount['account_kind'];
  suggested_number: string;
  account_name: string;
  account_description?: string;
  finsta_cate_id: number;
}

export interface CreateFromTemplateResult {
  ok: boolean;
  headers_created: number;
  accounts_created: number;
  skipped: number;
}

/**
 * Instantiate the staged (possibly edited) account list into acctg_coa for one owner.
 * Builds a header + child tree, skipping any accounts that already exist for that owner
 * (safe to re-run to add only the missing accounts).
 */
export function createCoaFromTemplate(params: {
  owner_type: string;
  owner_id: number;
  busi_type_id?: number;
  accounts: CreateFromTemplateAccount[];
}): Promise<CreateFromTemplateResult> {
  return call<CreateFromTemplateResult>(`/coa/from-template`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Set (or clear, with busi_type_id = null) a business entity's industry type.
 * Owner-scoped endpoint — works for any account owner, not just site admins.
 */
export function setBusinessType(params: {
  owner_type: 'business' | 'client_business';
  owner_id: number;
  busi_type_id: number | null;
}): Promise<{ ok: boolean; business_id: number; industry_type_id: number | null }> {
  return call(`/business-type`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });
}
